import { Component, input, signal, effect, inject, DestroyRef } from '@angular/core';
import { SplitFlapAudioService } from '../../services/split-flap-audio.service';
import { DEFAULT_ALPHABET } from '../../models/board.model';
import { prefersReducedMotion } from '../../utils/reduced-motion';

@Component({
  selector: 'app-split-flap-character',
  standalone: true,
  imports: [],
  templateUrl: './split-flap-character.html',
  styleUrl: './split-flap-character.css',
})
export class SplitFlapCharacter {
  private audioService = inject(SplitFlapAudioService);

  // Target character (input from parent)
  targetChar = input.required<string>();

  // Stagger delay before starting animation (input from parent)
  staggerDelay = input<number>(0);

  // Suppress flip audio for this character, regardless of the global mute setting
  muted = input<boolean>(false);

  // Characters on this flap's drum. A shorter drum means shorter flips.
  alphabet = input<string>(DEFAULT_ALPHABET);

  // Hold the drum where it is. The flap finishes the flip it is on, then waits
  // between steps, so resuming continues the same sequence from the character
  // it stopped on rather than restarting or jumping to the target.
  paused = input<boolean>(false);

  // Current displayed character
  protected readonly currentChar = signal(' ');

  // Next character (for the flip animation)
  protected readonly nextChar = signal(' ');

  // Is currently flipping
  protected readonly isFlipping = signal(false);

  // Track if we're in the middle of a flip sequence
  private isAnimating = false;

  // Abort flag for canceling in-progress animations
  private currentAnimationId = 0;

  // Pending timers, tracked so destroy can cancel them. A flap left running
  // after its component is gone keeps flipping and clacking on a page the
  // visitor has already left.
  private staggerTimer: ReturnType<typeof setTimeout> | null = null;
  private delayTimer: ReturnType<typeof setTimeout> | null = null;
  private delayResolve: (() => void) | null = null;

  // Flips waiting for the board to come back into view
  private resumeResolvers: Array<() => void> = [];

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cancelPendingWork());

    // Let any held flip carry on the moment the board is visible again
    effect(() => {
      if (!this.paused()) this.releaseHeldFlips();
    });

    // Watch for target character changes
    effect(() => {
      const target = this.targetChar().toUpperCase();

      // Reduced motion: drop the stagger too, so the whole message lands in one
      // update instead of wiping across in a mix of the old and new words
      const stagger = this.staggerDelay();
      const delay = prefersReducedMotion() ? 0 : stagger;

      // Cancel any in-progress animation and capture the new ID
      this.currentAnimationId++;
      const expectedAnimationId = this.currentAnimationId;

      // Apply stagger delay before starting flip
      if (this.staggerTimer) clearTimeout(this.staggerTimer);
      this.staggerTimer = setTimeout(() => {
        this.staggerTimer = null;
        // Only start flip if this is still the current animation request
        if (expectedAnimationId === this.currentAnimationId) {
          this.flipTo(target, expectedAnimationId);
        }
      }, delay);
    });
  }

  /**
   * Cancel any pending or in-flight flip. Bumping the animation id makes the
   * running flipTo loop bail at its next checkpoint; resolving the outstanding
   * delay lets it reach that checkpoint immediately instead of hanging.
   */
  private cancelPendingWork(): void {
    this.currentAnimationId++;

    if (this.staggerTimer) clearTimeout(this.staggerTimer);
    this.staggerTimer = null;

    if (this.delayTimer) clearTimeout(this.delayTimer);
    this.delayTimer = null;

    this.delayResolve?.();
    this.delayResolve = null;

    // Let held flips run on so they reach their abort check instead of hanging
    this.releaseHeldFlips();
  }

  private releaseHeldFlips(): void {
    const held = this.resumeResolvers;
    this.resumeResolvers = [];
    held.forEach((resolve) => resolve());
  }

  /**
   * Resolves once the board is visible. Called between flips, so a flap holds
   * on a whole character rather than freezing part-way through a rotation.
   */
  private untilVisible(): Promise<void> {
    if (!this.paused()) return Promise.resolve();
    return new Promise((resolve) => this.resumeResolvers.push(resolve));
  }

  /**
   * Flip to a target character by cycling through all intermediate characters
   * @param targetChar The character to flip to
   * @param expectedAnimationId The animation ID that should be current for this animation to proceed
   */
  private async flipTo(targetChar: string, expectedAnimationId: number): Promise<void> {
    const alphabet = this.alphabet();

    // Validate target character
    if (!alphabet.includes(targetChar)) {
      console.warn(`Invalid character: ${targetChar}, defaulting to space`);
      targetChar = ' ';
    }

    // If already at target, no need to flip
    if (this.currentChar() === targetChar) {
      return;
    }

    // If this animation has already been superseded, don't start
    if (expectedAnimationId !== this.currentAnimationId) {
      return;
    }

    // Reduced motion: settle straight to the target with no cycling or sound
    if (prefersReducedMotion()) {
      this.nextChar.set(targetChar);
      this.currentChar.set(targetChar);
      this.isFlipping.set(false);
      return;
    }

    // Mark that we're animating
    this.isAnimating = true;

    // Guard against a current character that is not on this drum (e.g. the
    // alphabet changed after the flap had already settled). There is no drum
    // position to rotate from, so seat the flap on the blank at index 0 and
    // cycle from there - rotating out of a position the drum does not have
    // would skip every intermediate character.
    let startIdx = alphabet.indexOf(this.currentChar());
    if (startIdx === -1) {
      startIdx = 0;
      this.currentChar.set(alphabet[0]);
      this.nextChar.set(alphabet[0]);
    }
    const endIdx = alphabet.indexOf(targetChar);

    // Calculate shortest path (may wrap around)
    let steps = endIdx - startIdx;
    if (steps < 0) {
      steps += alphabet.length;
    }

    // Flip through each intermediate character
    for (let i = 1; i <= steps; i++) {
      // Hold here while the board is off screen, so the sequence picks up on
      // the character it stopped on
      await this.untilVisible();

      // Check if this animation has been aborted
      if (expectedAnimationId !== this.currentAnimationId) {
        // Another animation has started, abort this one
        this.isFlipping.set(false);
        return;
      }

      const nextIdx = (startIdx + i) % alphabet.length;
      const nextCharacter = alphabet[nextIdx];

      // Set the next character (for the back of the flap and static card)
      this.nextChar.set(nextCharacter);

      // Play flip sound
      if (!this.muted()) {
        this.audioService.playFlip();
      }

      // Start flip animation
      this.isFlipping.set(true);

      // Wait for flip animation to complete (250ms)
      await this.delay(250);

      // Check again after delay in case we were aborted during animation
      if (expectedAnimationId !== this.currentAnimationId) {
        this.isFlipping.set(false);
        return;
      }

      // Update current character to next (after animation completes)
      this.currentChar.set(nextCharacter);

      // End flip animation
      this.isFlipping.set(false);

      // Brief pause between flips for "clack-clack" rhythm (50ms)
      await this.delay(50);
    }

    // Only clear animating flag if we weren't aborted
    if (expectedAnimationId === this.currentAnimationId) {
      this.isAnimating = false;
    }
  }

  /**
   * Utility function to create a delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.delayResolve = resolve;
      this.delayTimer = setTimeout(() => {
        this.delayTimer = null;
        this.delayResolve = null;
        resolve();
      }, ms);
    });
  }
}
